import { useRef, useState } from "react";
import { Play } from "lucide-react";

/**
 * Reusable video player with a large centered play button overlay.
 * Overlay disappears once playback starts; native controls take over.
 */
export default function VideoPlayer({
  src,
  poster,
  className = "",
  testid,
  ariaLabel = "Play video",
}) {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);

  const handlePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {});
    setStarted(true);
  };

  return (
    <div className={`relative overflow-hidden bg-black ${className}`}>
      <video
        ref={videoRef}
        data-testid={testid}
        src={src}
        poster={poster}
        controls={started}
        preload="metadata"
        playsInline
        onPlay={() => setStarted(true)}
        onPause={() => {
          const v = videoRef.current;
          if (v && v.currentTime === 0) setStarted(false);
        }}
        className="w-full h-full object-cover"
      />
      {!started && (
        <button
          type="button"
          onClick={handlePlay}
          aria-label={ariaLabel}
          data-testid={testid ? `${testid}-play` : undefined}
          className="absolute inset-0 flex items-center justify-center group focus:outline-none"
        >
          {/* subtle darkening for legibility */}
          <span className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
          <span className="relative flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-faytex-pink text-white shadow-hover transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
            {/* soft pink pulse ring */}
            <span className="absolute inset-0 rounded-full bg-faytex-pink opacity-40 animate-ping" />
            <Play className="w-8 h-8 md:w-10 md:h-10 ml-1 relative z-10" fill="currentColor" />
          </span>
        </button>
      )}
    </div>
  );
}
